import React, { useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FiArrowLeft, FiArrowRight, FiUpload, FiFileText, FiCopy, FiUsers, FiFolder, FiDownload, FiCheck, FiAlertCircle, FiChevronDown, FiClock, FiEye } from 'react-icons/fi';
import { IGlobalState } from '../../../../model/IGlobalState';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import { toast } from '../../Toast';
import { useContactsData, listName } from './contactsData';
import { CONSENT_SOURCES } from './AddContactModal';
import { ImportField, ImportTable, IMPORT_FIELDS, parseCsv, parsePasted, guessFields, importPeople, templateCsv, downloadText } from './importCsv';

type Method = 'file' | 'paste' | 'register';
const STEPS = ['Upload', 'Match columns', 'Settings', 'Review'];
const MAX_ROWS = 10000;

// Communications › Contacts › Import Contacts: Upload (spec 589:4827), Match columns (589:5423), Settings (589:5975), Review (589:6540).
// The file is read in the browser and checked against the real contacts; the final Import is preview only — nothing is added.
const ImportContacts: React.FC = () => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const user = useSelector((state: IGlobalState) => state.user);
    const company = stock?.companyName || stock?.ticker || '';
    const short = company.replace(/\s+(limited|ltd\.?)$/i, '');
    const recorder = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;
    const { data, error } = useContactsData(stock?.id);

    const [step, setStep] = useState(0);
    const [method, setMethod] = useState<Method>('file');
    const [fileTable, setFileTable] = useState<ImportTable | null>(null);
    const [pasted, setPasted] = useState('');
    const [over, setOver] = useState(false);
    const [fields, setFields] = useState<ImportField[]>([]);
    const [lists, setLists] = useState<string[] | null>(null);
    const [tagOn, setTagOn] = useState(true);
    const [skipUnsub, setSkipUnsub] = useState(true);
    const [matchRegister, setMatchRegister] = useState(true);
    const [updateExisting, setUpdateExisting] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [how, setHow] = useState(CONSENT_SOURCES[1]);
    const fileInput = useRef<HTMLInputElement>(null);

    const tag = `CSV import ${moment().format('MMM YYYY')}`;
    const pastedTable = useMemo(() => pasted.trim() ? parsePasted(pasted) : null, [pasted]);
    const table = method === 'file' ? fileTable : method === 'paste' ? pastedTable : null;
    // default: the default (ASX announcement) list, until the user picks
    const chosenLists = lists ?? (data ? [data.defaultListId ?? data.lists[0]?.id].filter((id): id is string => !!id) : []);

    const readFile = (file: File) => {
        if (!/\.csv$/i.test(file.name)) {
            toast.error('CSV only for now — save the spreadsheet as .csv and choose it again');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const all = parseCsv(String(reader.result ?? ''));
            if (all.length < 2) { toast.error(`${file.name} has no rows under the column names`); return; }
            if (all.length - 1 > MAX_ROWS) { toast.error(`${file.name} has ${(all.length - 1).toLocaleString()} rows — the limit is ${MAX_ROWS.toLocaleString()}`); return; }
            const [head, ...rows] = all;
            const width = Math.max(head.length, ...rows.map(r => r.length));
            const columns = Array.from({ length: width }, (_, i) => head[i] || `Column ${i + 1}`);
            setFileTable({ source: file.name, size: file.size, columns, rows });
        };
        reader.onerror = () => toast.error(`Couldn’t read ${file.name}`);
        reader.readAsText(file);
    };

    const pick = (m: Method) => {
        if (m === 'register') { toast.info('Preview only: importing from the share register isn’t connected yet'); return; }
        setMethod(m);
    };

    const next = () => {
        if (step === 0 && table) setFields(guessFields(table));
        setStep(s => s + 1);
    };
    const toList = () => history.push('/communications/contacts');

    const emailCols = fields.filter(f => f === 'email').length;
    const result = useMemo(() => table && fields.length ? importPeople(table, fields) : null, [table, fields]);

    // the four Review numbers, against the contacts already loaded
    const review = useMemo(() => {
        if (!result || !data) return null;
        const byEmail = new Map(data.contacts.map(c => [c.email.toLowerCase(), c]));
        const rows = result.people.map(p => {
            const existing = byEmail.get(p.email.toLowerCase());
            const status: 'new' | 'already' | 'skipped' = !existing ? 'new' : skipUnsub && !existing.subscribed ? 'skipped' : 'already';
            return { ...p, name: p.name || existing?.name || '', status };
        });
        return {
            rows,
            added: rows.filter(r => r.status === 'new').length,
            already: rows.filter(r => r.status === 'already').length,
            skipped: rows.filter(r => r.status === 'skipped').length,
        };
    }, [result, data, skipUnsub]);

    const canContinue = step === 0 ? !!table && table.rows.length > 0
        : step === 1 ? emailCols === 1
            : step === 2 ? agreed && chosenLists.length > 0 && !!data
                : false;
    const importCount = review?.added ?? 0;
    const canImport = !!review && (importCount > 0 || (updateExisting && review.already > 0));

    const runImport = () => {
        toast.info(`Preview only: ${importCount.toLocaleString()} contact${importCount === 1 ? '' : 's'} weren’t imported`);
        toList();
    };

    const footNote = step === 0 ? 'Nothing is added to your contacts yet.'
        : step === 1 ? result && result.duplicates > 0 ? `${result.duplicates} row${result.duplicates === 1 ? '' : 's'} look${result.duplicates === 1 ? 's' : ''} like duplicates — Investi will merge them.` : ''
            : step === 2 ? `The permission declaration is recorded against all ${(result?.people.length ?? 0).toLocaleString()} contacts.`
                : `This is the last step — importing adds ${importCount.toLocaleString()} ${importCount === 1 ? 'person' : 'people'} to your contacts.`;

    const listNames = data ? data.lists.filter(l => chosenLists.includes(l.id)).map(listName) : [];
    const savedAs = [...listNames, ...(tagOn ? [tag] : [])].join(' · ');

    const head = (icon: React.ReactNode, title: string, sub: string) =>
        <div className="contacts-card-head"><span className="icon-tile">{icon}</span><div><h2>{title}</h2><p>{sub}</p></div></div>;
    const tick = (label: string, on: boolean, set: (v: boolean) => void) =>
        <label className="contacts-tick-row"><input type="checkbox" checked={on} onChange={e => set(e.target.checked)} /><span>{label}</span></label>;
    const methodCard = (m: Method, icon: React.ReactNode, title: string, sub: string) =>
        <button type="button" className={`contacts-method${method === m ? ' on' : ''}`} onClick={() => pick(m)} aria-pressed={method === m}>
            <span className="contacts-method-icon">{icon}</span>
            <span className="contacts-method-t"><strong>{title}</strong><span>{sub}</span></span>
            <span className="contacts-radio">{method === m && <FiCheck />}</span>
        </button>;

    return <div className="contacts-import">
        <button type="button" className="link contacts-back" onClick={toList}><FiArrowLeft /> Back to Contacts</button>
        <div className="contacts-import-head">
            <div>
                <h1>Import Contacts</h1>
                <p>Bring a list of people into Investi. You can stop at any point — nothing is added until the last step.</p>
            </div>
            <button type="button" className="btn-outline" onClick={toList}>Cancel</button>
        </div>

        <ol className="card contacts-steps">
            {STEPS.map((s, i) => <li key={s} className={i < step ? 'done' : i === step ? 'current' : ''}>
                {i > 0 && <span className="contacts-step-line" />}
                <button type="button" disabled={i >= step} onClick={() => setStep(i)}>
                    <span className="contacts-step-n">{i < step ? <FiCheck /> : i + 1}</span>{s}
                </button>
            </li>)}
        </ol>

        {error && <p className="error">{error}</p>}

        {step === 0 && <div className="card contacts-import-card">
            {head(<FiUpload />, 'How Do You Want to Add Them?', 'Most people upload a spreadsheet exported from their old system')}
            <div className="contacts-methods">
                {methodCard('file', <FiFileText />, 'Upload a CSV file', 'A spreadsheet with one person per row')}
                {methodCard('paste', <FiCopy />, 'Paste them in', 'Copy and paste names and email addresses')}
                {methodCard('register', <FiUsers />, 'From the share register', 'Holders who have an email address on the register')}
            </div>
            {method === 'file' && <div className={`contacts-drop${over ? ' over' : ''}${fileTable ? ' has-file' : ''}`}
                onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
                onDrop={e => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) readFile(f); }}>
                <span className="icon-tile"><FiUpload /></span>
                {fileTable
                    ? <><strong>{fileTable.source}</strong><span>{fileTable.rows.length.toLocaleString()} rows · {fileTable.columns.length} columns — drop another file to replace it</span></>
                    : <><strong>Drag your file here</strong><span>CSV · up to {MAX_ROWS.toLocaleString()} rows · first row should be the column names</span></>}
                <button type="button" className="btn-outline" onClick={() => fileInput.current?.click()}><FiFolder /> {fileTable ? 'Choose a Different File' : 'Choose a File'}</button>
                <input ref={fileInput} type="file" accept=".csv,text/csv,.xlsx,.xls" hidden onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ''; }} />
            </div>}
            {method === 'paste' && <label className="contacts-paste">
                <span>One person per line — <code>Name &lt;email&gt;</code> or just the email address</span>
                <textarea rows={8} value={pasted} placeholder={'John Citizen <john.citizen@example.com>\njane@example.com'} onChange={e => setPasted(e.target.value)} />
                {pastedTable && <small>{pastedTable.rows.length.toLocaleString()} line{pastedTable.rows.length === 1 ? '' : 's'}</small>}
            </label>}
            <div className="contacts-template">
                <FiFileText />
                <span>Not sure how to lay out the file? Download our template — it has the columns Investi expects.</span>
                <button type="button" className="btn-outline" onClick={() => downloadText('investi-contacts-template.csv', templateCsv())}><FiDownload /> Download Template</button>
            </div>
        </div>}

        {step === 1 && table && <>
            <div className="card contacts-import-card contacts-file">
                {head(<FiFileText />, table.source, `${table.rows.length.toLocaleString()} rows · ${table.columns.length} columns${table.size !== undefined ? ` · ${Math.max(1, Math.round(table.size / 1024))} KB` : ''}`)}
            </div>
            <div className="card contacts-import-card">
                {head(<FiCopy />, 'Match Your Columns', 'Tell Investi which column is which. We have guessed — check the ones marked.')}
                <div className="contacts-map">
                    {table.columns.map((col, i) => {
                        const sample = table.rows.map(r => r[i]).find(v => !!v);
                        return <div key={i} className="contacts-map-row">
                            <div className="contacts-map-col"><strong>{col}</strong>{sample && <span>e.g. {sample}</span>}</div>
                            <FiArrowRight className="contacts-map-arrow" />
                            <span className={`contacts-select${fields[i] !== 'skip' ? ' set' : ''}`}>
                                <select aria-label={`${col} is`} value={fields[i]} onChange={e => { const v = e.target.value as ImportField; setFields(fs => fs.map((f, j) => j === i ? v : f)); }}>
                                    {IMPORT_FIELDS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                                <FiChevronDown />
                            </span>
                        </div>;
                    })}
                </div>
                <div className={`contacts-amber${emailCols === 1 ? '' : ' bad'}`}>
                    <FiAlertCircle />
                    <span>{emailCols === 0 ? 'Pick the column that holds the email address — it’s the only one Investi must have.'
                        : emailCols > 1 ? 'Only one column can be the email address.'
                            : 'Email address is the only column Investi must have. Anything set to ‘Don’t import’ is ignored.'}
                        {emailCols === 1 && result && result.invalid > 0 && ` ${result.invalid} row${result.invalid === 1 ? ' has' : 's have'} no valid email address and will be left out.`}</span>
                </div>
            </div>
        </>}

        {step === 2 && <>
            <div className="card contacts-import-card">
                {head(<FiUsers />, 'Where They Go', 'Pick the lists and a tag so you can find this batch later')}
                {!data ? <p className="loading">Loading lists…</p> : <div className="contacts-checks">
                    {data.lists.map(l => {
                        const on = chosenLists.includes(l.id);
                        return <label key={l.id} className={`contacts-check-row${on ? ' on' : ''}`}>
                            <input type="checkbox" checked={on} onChange={() => setLists(on ? chosenLists.filter(x => x !== l.id) : [...chosenLists, l.id])} />
                            <span>{listName(l)}</span>
                        </label>;
                    })}
                </div>}
                <div className="contacts-ticks">
                    {tick(`Tag them as “${tag}”`, tagOn, setTagOn)}
                    {tick('Skip anyone who has unsubscribed before (recommended)', skipUnsub, setSkipUnsub)}
                    {tick('Try to match them to the share register', matchRegister, setMatchRegister)}
                    {tick('Update details for people already in Contacts', updateExisting, setUpdateExisting)}
                </div>
            </div>
            <div className="contacts-import-permission">
                <div className="contacts-perm-head"><ShieldCheckIcon /> <strong>Permission to email everyone in this file</strong> <span className="contacts-req">Required</span></div>
                <div className="contacts-perm-body">
                    <label className={`contacts-confirm${agreed ? ' on' : ''}`}>
                        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                        <span><strong>I confirm every person in this file has agreed to receive investor emails from {company}.</strong>
                            <small>Importing a bought or scraped list breaches the Spam Act 2003 (Cth). Investi records this declaration against the import.</small></span>
                    </label>
                    <div className="contacts-how">
                        <span>How did they agree?</span>
                        <span className="contacts-select">
                            <select aria-label="How did they agree?" value={how} onChange={e => setHow(e.target.value)}>{CONSENT_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}</select>
                            <FiChevronDown />
                        </span>
                        <p><FiClock /> Recorded against all {(result?.people.length ?? 0).toLocaleString()} contacts in this file as: {recorder} · {moment().format('D MMMM YYYY')} · CSV import</p>
                    </div>
                </div>
            </div>
        </>}

        {step === 3 && review && result && table && <>
            <div className="contacts-review-stats">
                <div><div className="v">{review.added.toLocaleString()}</div><div className="l">new contacts</div><div className="d">will be added</div></div>
                <div><div className="v">{review.already.toLocaleString()}</div><div className="l">already in Contacts</div><div className="d">{updateExisting ? 'details merged' : 'left as they are'}</div></div>
                <div><div className="v">{review.skipped.toLocaleString()}</div><div className="l">skipped</div><div className="d">previously unsubscribed</div></div>
                <div><div className="v">{table.rows.length.toLocaleString()}</div><div className="l">rows in the file</div>
                    <div className="d">{[result.invalid ? `${result.invalid} without a valid email` : '', result.duplicates ? `${result.duplicates} duplicate${result.duplicates === 1 ? '' : 's'} merged` : ''].filter(Boolean).join(' · ')}</div></div>
            </div>
            <div className="card contacts-import-card">
                {head(<FiEye />, 'Check a Few Rows', 'The first five, exactly as they will be saved')}
                <div className="contacts-preview">
                    {review.rows.slice(0, 5).map(r => <div key={r.email} className="contacts-preview-row">
                        <div className="contacts-preview-who"><strong>{r.name || r.email}</strong>{r.name && <span>{r.email}</span>}</div>
                        <span className="contacts-preview-where">{r.status === 'skipped' ? 'Not imported' : savedAs}</span>
                        {r.status === 'new' && <span className="pill ok">New</span>}
                        {r.status === 'already' && <span className="pill">{updateExisting ? 'Already there — merged' : 'Already there'}</span>}
                        {r.status === 'skipped' && <span className="pill bad">Skipped — unsubscribed</span>}
                    </div>)}
                    {review.rows.length === 0 && <p className="contacts-empty">No rows with a valid email address</p>}
                </div>
            </div>
            <div className="contacts-confirmed"><FiCheck /> You confirmed everyone in this file agreed to hear from {short}. That declaration is saved against each contact with your name and today’s date.</div>
        </>}

        <div className="contacts-import-foot">
            <span>{footNote}</span>
            {step > 0 && <button type="button" className="btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
            {step < 3
                ? <button type="button" className="btn-primary" disabled={!canContinue} onClick={next}><FiArrowRight /> Continue</button>
                : <button type="button" className="btn-primary" disabled={!canImport} onClick={runImport}><FiUpload /> Import {importCount.toLocaleString()} Contact{importCount === 1 ? '' : 's'}</button>}
        </div>
    </div>;
};

export default ImportContacts;
